/**
 * Sanitizes rich-text HTML (as produced by the app's rich-text editor) while keeping its formatting:
 * inline styles (alignment, colour, font, spacing), lists, tables, links and images survive; scripts,
 * event handlers, unsafe URLs and unknown elements do not. Angular's built-in sanitizer strips every
 * `style` attribute, which loses the formatting the author applied.
 */
const DROP_WITH_CONTENT = new Set(['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'base', 'form', 'input', 'button', 'textarea', 'select', 'svg', 'math', 'template', 'noscript', 'title']);

const ALLOWED_TAGS = new Set([
  'a', 'abbr', 'b', 'blockquote', 'br', 'caption', 'code', 'col', 'colgroup', 'div', 'em', 'figcaption', 'figure', 'font',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'ol', 'p', 'pre', 's', 'small', 'span', 'strike', 'strong',
  'sub', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'u', 'ul', 'mark', 'del', 'ins',
]);

const GLOBAL_ATTRS = new Set(['class', 'style', 'align', 'dir', 'title', 'lang']);
const TAG_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'target', 'rel']),
  img: new Set(['src', 'alt', 'width', 'height']),
  font: new Set(['color', 'face', 'size']),
  td: new Set(['colspan', 'rowspan', 'width', 'height', 'valign']),
  th: new Set(['colspan', 'rowspan', 'width', 'height', 'valign']),
  table: new Set(['border', 'cellpadding', 'cellspacing', 'width']),
  ol: new Set(['start', 'type']),
  ul: new Set(['type']),
  col: new Set(['span', 'width']),
};

const ALLOWED_CSS = /^(color|background(-color)?|font(-family|-size|-weight|-style)?|text-(align|decoration|indent|transform)|line-height|letter-spacing|word-spacing|white-space|vertical-align|list-style(-type)?|margin(-(top|right|bottom|left))?|padding(-(top|right|bottom|left))?|border(-(top|right|bottom|left))?(-(width|style|color))?|border-collapse|width|height|max-width|display)$/;

function safeStyle(style: string): string {
  return style
    .split(';')
    .map(rule => rule.trim())
    .filter(rule => {
      const index = rule.indexOf(':');
      if (index < 1) return false;
      const property = rule.slice(0, index).trim().toLowerCase();
      const value = rule.slice(index + 1).trim().toLowerCase();
      if (!ALLOWED_CSS.test(property)) return false;
      return !/url\s*\(|expression|javascript:|@import|behavior|<|>/.test(value);
    })
    .join('; ');
}

function safeUrl(value: string, kind: 'link' | 'image'): boolean {
  const url = value.trim().replace(/[\u0000-\u001f\s]+/g, '');
  if (kind === 'image') return /^(https?:\/\/|\/|data:image\/(png|jpe?g|gif|webp|bmp);base64,)/i.test(url);
  return /^(https?:\/\/|mailto:|tel:|#|\/)/i.test(url);
}

function clean(node: Node, doc: Document): void {
  Array.from(node.childNodes).forEach(child => {
    if (child.nodeType === Node.COMMENT_NODE) { node.removeChild(child); return; }
    if (child.nodeType !== Node.ELEMENT_NODE) return;
    const element = child as HTMLElement;
    const tag = element.tagName.toLowerCase();

    if (DROP_WITH_CONTENT.has(tag)) { node.removeChild(element); return; }
    if (!ALLOWED_TAGS.has(tag)) {
      // Unknown element: keep its (cleaned) children in place of it.
      clean(element, doc);
      while (element.firstChild) node.insertBefore(element.firstChild, element);
      node.removeChild(element);
      return;
    }

    Array.from(element.attributes).forEach(attribute => {
      const name = attribute.name.toLowerCase();
      const allowed = GLOBAL_ATTRS.has(name) || TAG_ATTRS[tag]?.has(name);
      if (!allowed || name.startsWith('on')) { element.removeAttribute(attribute.name); return; }
      if (name === 'style') {
        const style = safeStyle(attribute.value);
        if (style) element.setAttribute('style', style); else element.removeAttribute('style');
      } else if (name === 'href' && !safeUrl(attribute.value, 'link')) {
        element.removeAttribute('href');
      } else if (name === 'src' && !safeUrl(attribute.value, 'image')) {
        element.removeAttribute('src');
      }
    });

    if (tag === 'a' && element.getAttribute('target') === '_blank') element.setAttribute('rel', 'noopener noreferrer');
    clean(element, doc);
  });
}

export function sanitizeRichText(html: string | null | undefined): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html'); // inert: nothing executes while parsing
  clean(doc.body, doc);
  return doc.body.innerHTML;
}

/** `<font size>` keywords (1-7) as the editor's execCommand emits them, mapped to pixel sizes. */
const FONT_TAG_SIZES: Record<string, string> = { '1': '10px', '2': '13px', '3': '16px', '4': '18px', '5': '24px', '6': '32px', '7': '48px' };

/**
 * The editor writes font sizes as `<font size="5">`, which the backend sanitizer drops; it keeps
 * `style="font-size: …"`. Rewrites every sized `<font>` as a `<span>` with an inline font-size.
 */
export function fontTagsToSpanStyles(html: string | null | undefined): string {
  if (!html || !/<font\b/i.test(html)) return html ?? '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.body.querySelectorAll('font').forEach(font => {
    const size = FONT_TAG_SIZES[font.getAttribute('size') ?? ''];
    const span = doc.createElement('span');
    if (size) span.style.fontSize = size;
    while (font.firstChild) span.appendChild(font.firstChild);
    font.replaceWith(span);
  });
  return doc.body.innerHTML;
}
